import { useMemo } from "react";
import startCase from "lodash/startCase";
import toLower from "lodash/toLower";

import { type Permission } from "@vega-api/generated/types/permission";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { ModalHeader } from "@vega-core/components/Modals/ModalHeader/ModalHeader";
import { VegaDialog } from "@vega-core/components/Modals/VegaDialog/VegaDialog";
import { Switch } from "@vega-core/components/Switch/Switch";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { PermissionsExpandToggle } from "@vega-modules/components/PermissionsExpandToggle/PermissionsExpandToggle";
import { useExpandableList } from "@vega-modules/hooks/useExpandableList";

import { FundLogos } from "../../../../../components/AssetClassLogos/AssetClassImages";

type Props = {
  open: boolean;
  onClose: VoidFunction;
  role: RoleNew;
  contextRoles: RoleNew[];
  icon?: string;
  subjectName: string;
};

export const ContextRolePermissionsModal = ({
  open,
  onClose,
  role,
  contextRoles,
  subjectName,
  icon,
}: Props) => {
  const allContextPermissions = useMemo(() => {
    const permissionsMap: Record<string, Permission> = {};

    for (const roleData of contextRoles) {
      for (const permission of roleData.permissions) {
        permissionsMap[permission.featureName] = permission;
      }
    }

    return permissionsMap;
  }, [contextRoles]);

  const currentRolePermissionNames = useMemo(
    () => new Set(role.permissions.map((p) => p.featureName)),
    [role.permissions],
  );

  const permissionsList = Object.values(allContextPermissions);
  const {
    visibleItems: visiblePermissions,
    hasMore,
    remainingCount,
    isExpanded,
    toggle,
  } = useExpandableList({ items: permissionsList, resetWhen: open });

  return (
    <VegaDialog
      dataTestId="context-role-permissions-dialog"
      onClose={onClose}
      open={open}
    >
      <ModalHeader
        title={m.userManagement_permissions_roles_rolePermissions()}
      />
      <div className="body-base-regular text-text/secondary">
        <p>
          {m.userManagement_permissions_viewRoleDescription()}{" "}
          <span className="body-base-semibold text-text/primary">
            {startCase(toLower(role.name))}
          </span>
        </p>
      </div>
      <div className="py-3 border-b border-border/secondary flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex flex-shrink-0 rounded-lg justify-center items-center overflow-hidden">
            {icon ? (
              <img alt={subjectName} className="w-8 h-8" src={icon} />
            ) : (
              <FundLogos assetClass="PRIVATE_EQUITY" isLoading={false} />
            )}
          </div>
          <div className="body-base-semibold text-text/primary">
            {subjectName}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 mt-4">
        {visiblePermissions.map((permission) => {
          const isCurrentRolePermission = currentRolePermissionNames.has(
            permission.featureName,
          );

          return (
            <div
              className="flex justify-between items-center py-3"
              key={permission.id}
            >
              <span
                className={cnTwMerge(
                  "body-base-medium",
                  isCurrentRolePermission
                    ? "text-text/primary"
                    : "text-text/secondary",
                )}
              >
                {permission.description}
              </span>
              <Switch
                checked={isCurrentRolePermission}
                disabled
                id={`permission-${permission.id}`}
              />
            </div>
          );
        })}
        <PermissionsExpandToggle
          className="py-2 -mb-2"
          dataTestId="permissions-expand-toggle"
          hasMore={hasMore}
          isExpanded={isExpanded}
          onToggle={toggle}
          remainingCount={remainingCount}
        />
      </div>
    </VegaDialog>
  );
};
